/**
 * 3D input: raycast picking against the live renderer → Commands.
 *
 * Left-click: select own unit/building (shift adds). Left-drag: box-select all
 * own units inside the rectangle (shift adds). Right-click: contextual command
 * (enemy→attack, resource→gather, own foundation→construct, else→move).
 * Build mode: left-click places; right-click/Escape cancels.
 * Mouse hover drives the renderer's build-preview ghost + validity.
 */
import type { World } from "../sim/world";
import type { Registry } from "../data/registry";
import type { Command, Eid } from "../sim/types";
import type { Renderer3d } from "../render/renderer3d";
import { canPlaceBuilding } from "../sim/commands";
import { CURSORS, type CursorAction } from "./cursors";

export interface InputCallbacks {
  localPlayer(): number;
  world(): World;
  reg(): Registry;
  renderer(): Renderer3d;
  onCommand(cmd: Command): void;
  onSelectionChange(sel: Set<Eid>): void;
  onBuildModeChange(b: string | null): void;
}

const DRAG_THRESHOLD = 6; // px before a left-press becomes a box drag

export class Input3d {
  readonly selection = new Set<Eid>();
  buildMode: string | null = null;
  /** For line-placed buildings (walls): the first-clicked anchor hex. The second
   *  click lays a continuous run of segments from here to the cursor. */
  private lineAnchor: { col: number; row: number } | null = null;
  private cbs: InputCallbacks;

  private dragStart: { x: number; y: number } | null = null;
  private dragShift = false;
  private dragging = false;
  private readonly box: HTMLElement;

  constructor(cbs: InputCallbacks) {
    this.cbs = cbs;
    const dom = cbs.renderer().renderer.domElement;
    this.box = document.createElement("div");
    this.box.className = "drag-box";
    this.box.style.display = "none";
    document.body.appendChild(this.box);

    dom.addEventListener("contextmenu", (e) => e.preventDefault());
    dom.addEventListener("mousedown", (e) => this.onDown(e.button, e.clientX, e.clientY, e.shiftKey));
    dom.addEventListener("mousemove", (e) => this.onHover(e.clientX, e.clientY));
    // drag move/up on window so the box keeps tracking outside the canvas
    addEventListener("mousemove", (e) => this.onDragMove(e.clientX, e.clientY));
    addEventListener("mouseup", (e) => { if (e.button === 0) this.onUp(e.clientX, e.clientY); });
  }

  /** Cancel the most recent in-world context: an active build placement first,
   *  then any unit selection. Returns true if something was cancelled (so the
   *  caller knows Escape was "used" and shouldn't also open the pause menu). */
  cancelContext(): boolean {
    if (this.lineAnchor) {
      this.lineAnchor = null;
      this.cbs.renderer().buildPreview = null;
      return true;
    } // drop the wall anchor first
    if (this.buildMode) { this.setBuildMode(null); return true; }
    if (this.selection.size > 0) { this.selection.clear(); this.cbs.onSelectionChange(this.selection); return true; }
    return false;
  }

  setBuildMode(b: string | null) {
    this.buildMode = b;
    this.lineAnchor = null;
    this.cbs.renderer().buildPreview = null;
    this.cbs.onBuildModeChange(b);
    this.updateCursor(this.cbs.renderer().hovered);
  }

  /** A wall is placed as a LINE (click a start tile, click again at the end). */
  private isLineBuild(): boolean {
    return this.buildMode === "wall";
  }

  /** Connected run of hexes from `a` to `b`: sample the straight world-space line
   *  and collect each hex it passes through, in order (no duplicates). */
  private lineHexes(a: { col: number; row: number }, b: { col: number; row: number }): [number, number][] {
    const world = this.cbs.world();
    const ca = world.hexCenter(a.col, a.row), cb = world.hexCenter(b.col, b.row);
    const dx = cb.x - ca.x, dy = cb.y - ca.y;
    const steps = Math.max(1, Math.round(Math.hypot(dx, dy) / (world.size * 0.5)));
    const out: [number, number][] = [];
    const seen = new Set<number>();
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const h = world.hexAtWorld(ca.x + dx * t, ca.y + dy * t);
      if (!h) continue;
      const k = world.hkey(h.col, h.row);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push([h.col, h.row]);
    }
    return out;
  }

  /** Validate a wall run against the resources left after each accepted segment. */
  private linePlacements(hexes: [number, number][]): { col: number; row: number; valid: boolean }[] {
    const world = this.cbs.world();
    const player = world.players.get(this.cbs.localPlayer());
    const id = this.buildMode;
    if (!player || !id) return [];
    const def = this.cbs.reg().building(player.civ, id);
    const remaining = { ...player.resources };
    return hexes.map(([col, row]) => {
      const affordable = Object.entries(def.cost).every(([resource, amount]) => (remaining[resource] ?? 0) >= (amount ?? 0));
      const valid = affordable && canPlaceBuilding(world, def, col, row);
      if (valid) for (const [resource, amount] of Object.entries(def.cost)) remaining[resource] = (remaining[resource] ?? 0) - (amount ?? 0);
      return { col, row, valid };
    });
  }

  private onDown(button: number, cx: number, cy: number, shift: boolean) {
    const renderer = this.cbs.renderer();
    const player = this.cbs.localPlayer();

    // BUILD MODE: left-click places at the hovered hex
    if (this.buildMode && button === 0) {
      const g = renderer.screenToGround(cx, cy);
      if (!g) return;
      const world = this.cbs.world();
      const workers = [...this.selection].filter((e) => this.owns(e));

      // WALL line tool: first click anchors the run, second click lays it.
      if (this.isLineBuild()) {
        const hex = world.hexAtWorld(g.x, g.z);
        if (!hex) return;
        if (!this.lineAnchor) { this.lineAnchor = { col: hex.col, row: hex.row }; return; }
        // lay one wall per hex along the line (queued so the workers chain through
        // them); the first respects shift so a non-shift run replaces current orders.
        const placements = this.linePlacements(this.lineHexes(this.lineAnchor, hex)).filter((p) => p.valid);
        placements.forEach(({ col: c, row: r }, i) => {
          const ctr = world.hexCenter(c, r);
          this.cbs.onCommand({ type: "build", player, workers, buildingId: this.buildMode!, x: ctr.x, y: ctr.y, queued: i === 0 ? shift : true });
        });
        this.lineAnchor = null;
        renderer.buildPreview = null;
        if (!shift) this.setBuildMode(null);
        return;
      }

      // shift = queue this build after current orders AND keep placing more.
      this.cbs.onCommand({ type: "build", player, workers, buildingId: this.buildMode, x: g.x, y: g.z, queued: shift });
      if (!shift) this.setBuildMode(null);
      return;
    }
    // right-click in build mode: cancel the pending wall anchor first, else exit.
    if (this.buildMode && button === 2) {
      if (this.lineAnchor) { this.lineAnchor = null; renderer.buildPreview = null; }
      else this.setBuildMode(null);
      return;
    }

    if (button === 0) {
      // arm a potential drag; actual selection happens on mouse-up so a click
      // and a box-drag share the same gesture start.
      this.dragStart = { x: cx, y: cy };
      this.dragShift = shift;
      this.dragging = false;
    } else if (button === 2) {
      this.onCommandClick(cx, cy, shift);
    }
  }

  private onUp(cx: number, cy: number) {
    if (!this.dragStart) return;
    const start = this.dragStart;
    const wasDragging = this.dragging;
    this.dragStart = null;
    this.dragging = false;
    this.box.style.display = "none";
    if (this.buildMode) return;
    if (wasDragging) this.boxSelect(start.x, start.y, cx, cy, this.dragShift);
    else this.clickSelect(cx, cy, this.dragShift);
  }

  private onDragMove(cx: number, cy: number) {
    if (!this.dragStart || this.buildMode) return;
    const dx = cx - this.dragStart.x;
    const dy = cy - this.dragStart.y;
    if (!this.dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    this.dragging = true;
    const minX = Math.min(this.dragStart.x, cx);
    const minY = Math.min(this.dragStart.y, cy);
    this.box.style.display = "block";
    this.box.style.left = minX + "px";
    this.box.style.top = minY + "px";
    this.box.style.width = Math.abs(dx) + "px";
    this.box.style.height = Math.abs(dy) + "px";
  }

  private clickSelect(cx: number, cy: number, shift: boolean) {
    const eid = this.cbs.renderer().pickEid(cx, cy);
    if (eid !== null && this.owns(eid)) {
      if (shift) this.selection.add(eid);
      else { this.selection.clear(); this.selection.add(eid); }
    } else if (eid !== null) {
      this.selection.clear();
      this.selection.add(eid); // enemy/neutral inspect-only
    } else if (!shift) {
      this.selection.clear();
    }
    this.cbs.onSelectionChange(this.selection);
    this.updateCursor(this.cbs.renderer().hovered);
  }

  private boxSelect(x0: number, y0: number, x1: number, y1: number, shift: boolean) {
    const world = this.cbs.world();
    const renderer = this.cbs.renderer();
    const minX = Math.min(x0, x1), maxX = Math.max(x0, x1);
    const minY = Math.min(y0, y1), maxY = Math.max(y0, y1);
    if (!shift) this.selection.clear();
    // test the unit's torso point (offset scaled to hex size, NOT a fixed 0.5
    // world units which floated the point well above the unit's head and made
    // the box miss most units unless drawn huge).
    for (const [eid, tr] of world.transform) {
      if (world.ident.get(eid)?.kind !== "unit" || !this.owns(eid)) continue;
      const h = renderer.heightAt(tr.pos.x, tr.pos.y) + world.size * 0.27;
      const s = renderer.worldToScreen(tr.pos.x, h, tr.pos.y);
      if (s.behind) continue;
      if (s.x >= minX && s.x <= maxX && s.y >= minY && s.y <= maxY) this.selection.add(eid);
    }
    this.cbs.onSelectionChange(this.selection);
    this.updateCursor(this.cbs.renderer().hovered);
  }

  private onCommandClick(cx: number, cy: number, queued: boolean) {
    const world = this.cbs.world();
    const renderer = this.cbs.renderer();
    const player = this.cbs.localPlayer();
    const actors = [...this.selection].filter((e) => this.isOwnUnit(e));
    const target = renderer.pickEid(cx, cy);

    // No own units selected but an own building is. A garrisoned tower with
    // occupants → eject them at the click; a production building → set its rally.
    if (actors.length === 0) {
      const garrisoned = [...this.selection].find(
        (e) => this.owns(e) && world.building.has(e) && (world.garrison.get(e)?.occupants.length ?? 0) > 0);
      if (garrisoned !== undefined) {
        const g = renderer.screenToGround(cx, cy);
        if (g) this.cbs.onCommand({ type: "unload", player, transport: garrisoned, x: g.x, y: g.z, queued });
        return;
      }
      const building = [...this.selection].find(
        (e) => this.owns(e) && world.producer.has(e));
      if (building === undefined) return;
      // a field is a "building" that carries a resource component — treat it as a
      // gatherable rally target too (not just resource-node entities).
      if (target !== null && world.resource.has(target)) {
        const rt = world.transform.get(target)!;
        this.cbs.onCommand({ type: "setRally", player, building, x: rt.pos.x, y: rt.pos.y, resource: target });
      } else {
        const g = renderer.screenToGround(cx, cy);
        if (g) this.cbs.onCommand({ type: "setRally", player, building, x: g.x, y: g.z });
      }
      return;
    }

    if (target !== null) {
      // own TRANSPORT ship + selected LAND units → board it (embark). The ship
      // itself, if also selected, is filtered out (it can't ride itself).
      if (world.garrison.has(target) && this.owns(target)) {
        const landers = actors.filter((e) => world.ident.get(e)?.cls !== "naval");
        if (landers.length) {
          this.cbs.onCommand({ type: "embark", player, entities: landers, transport: target, queued });
          return;
        }
      }
      const tIdent = world.ident.get(target);
      const tOwner = world.owner.get(target)?.player;
      // a live wild animal → hunt it (attack). Its carcass has a resource
      // component and falls through to the gather branch below.
      if (world.fauna.has(target)) {
        this.cbs.onCommand({ type: "attack", player, entities: actors, target, queued });
        return;
      }
      // resource node OR a field (a building with a resource component) → gather
      if (tIdent?.kind === "resource" || world.resource.has(target)) {
        this.cbs.onCommand({ type: "gather", player, entities: actors, target, queued });
        return;
      }
      if (tIdent?.kind === "building" && tOwner === player) {
        const b = world.building.get(target);
        if (b?.isFoundation) {
          this.cbs.onCommand({ type: "construct", player, workers: actors, target, queued });
          return;
        }
        // own finished drop-off building + carrying workers → return resources
        if (b?.isDropSite && actors.some((e) => (world.gatherer.get(e)?.carrying ?? 0) > 0)) {
          this.cbs.onCommand({ type: "returnResources", player, entities: actors, target, queued });
          return;
        }
      }
      if (tOwner !== undefined && tOwner !== player) {
        this.cbs.onCommand({ type: "attack", player, entities: actors, target, queued });
        return;
      }
    }
    const g = renderer.screenToGround(cx, cy);
    if (!g) return;
    // a LOADED transport told to right-click LAND disgorges its cargo there
    // (right-clicking WATER instead just sails it, via the move below).
    const transport = actors.find((e) => world.garrison.has(e) && (world.garrison.get(e)?.occupants.length ?? 0) > 0);
    if (transport !== undefined) {
      const hex = world.hexAtWorld(g.x, g.z);
      // unload onto walkable LAND only — clicking water (sail) or a blocked tile
      // (mountain/building) falls through to a normal move.
      if (hex && !world.isWater(hex.col, hex.row) && world.isPassable(hex.col, hex.row) && !world.solidHexKeys.has(world.hkey(hex.col, hex.row))) {
        this.cbs.onCommand({ type: "unload", player, transport, x: g.x, y: g.z, queued });
        return;
      }
    }
    this.cbs.onCommand({ type: "move", player, entities: actors, x: g.x, y: g.z, queued });
  }

  private onHover(cx: number, cy: number) {
    const renderer = this.cbs.renderer();
    if (this.buildMode) {
      renderer.hovered = null;
      const g = renderer.screenToGround(cx, cy);
      if (g) {
        const world = this.cbs.world();
        const hex = world.hexAtWorld(g.x, g.z);
        const center = hex ? world.hexCenter(hex.col, hex.row) : { x: g.x, y: g.z };
        const ps = world.players.get(this.cbs.localPlayer());
        const def = ps ? this.cbs.reg().building(ps.civ, this.buildMode) : null;
        const placeable = !!hex && !!def && canPlaceBuilding(world, def, hex.col, hex.row);
        const affordOne = this.canAfford(world, this.buildMode);
        renderer.buildPreview = {
          buildingId: this.buildMode,
          x: center.x, z: center.y,
          // green only when BOTH the tile is buildable AND the player can afford
          // it — a green ghost you couldn't pay for was misleading.
          valid: placeable && affordOne,
          // WALL line tool with an anchor down → preview the whole run of ghosts.
          line: this.isLineBuild() && this.lineAnchor && hex
            ? this.linePlacements(this.lineHexes(this.lineAnchor, { col: hex.col, row: hex.row })).map(({ col: c, row: r, valid }) => {
                const ct = world.hexCenter(c, r);
                return { x: ct.x, z: ct.y, valid };
              })
            : undefined,
        };
      }
      return;
    }
    renderer.buildPreview = null;
    // suppress hover-picking mid-drag (cheaper, avoids flicker)
    renderer.hovered = this.dragging ? null : renderer.pickEid(cx, cy);
    this.updateCursor(renderer.hovered);
  }

  /** Set the canvas cursor to reflect what a right-click would DO here, given the
   *  current selection + hovered target (move / gather / build / attack / embark /
   *  unload). Mirrors onCommandClick's decision so the cursor never lies. */
  private updateCursor(target: Eid | null): void {
    const dom = this.cbs.renderer().renderer.domElement;
    dom.style.cursor = CURSORS[this.hoverAction(target)];
  }

  private hoverAction(target: Eid | null): CursorAction {
    if (this.buildMode) return "build";
    const world = this.cbs.world();
    const player = this.cbs.localPlayer();
    const actors = [...this.selection].filter((e) => this.isOwnUnit(e));
    if (actors.length === 0) return "default"; // nothing actionable selected
    if (target !== null && world.transform.has(target)) {
      // own transport with cargo room + land units selected → board it
      if (world.garrison.has(target) && this.owns(target) && actors.some((e) => world.ident.get(e)?.cls !== "naval")) return "embark";
      const tIdent = world.ident.get(target);
      const tOwner = world.owner.get(target)?.player;
      if (world.fauna.has(target)) return "attack"; // hunt
      if (tIdent?.kind === "resource" || world.resource.has(target)) return "gather";
      if (tIdent?.kind === "building" && tOwner === player) {
        const b = world.building.get(target);
        if (b?.isFoundation) return "build";
        if (b?.isDropSite && actors.some((e) => (world.gatherer.get(e)?.carrying ?? 0) > 0)) return "gather";
      }
      if (tOwner !== undefined && tOwner !== player) return "attack";
    }
    // empty ground: a loaded transport selected → unload here, else move
    if (actors.some((e) => world.garrison.has(e) && (world.garrison.get(e)?.occupants.length ?? 0) > 0)) return "unload";
    return "move";
  }

  private canAfford(world: World, buildingId: string): boolean {
    const ps = world.players.get(this.cbs.localPlayer());
    if (!ps) return false;
    const def = this.cbs.reg().building(ps.civ, buildingId);
    for (const [r, amt] of Object.entries(def.cost))
      if ((ps.resources[r] ?? 0) < (amt ?? 0)) return false;
    return true;
  }

  private owns(eid: Eid): boolean {
    return this.cbs.world().owner.get(eid)?.player === this.cbs.localPlayer();
  }
  private isOwnUnit(eid: Eid): boolean {
    const w = this.cbs.world();
    return w.owner.get(eid)?.player === this.cbs.localPlayer() && w.ident.get(eid)?.kind === "unit";
  }
}
